import React from 'react'
import { getPrepDashboardRevisions } from '../../api/prep'
import usePrepDashboardStore from '../../store/prepDashboardStore'

const MIN_POLL_DELAY_MS = 28_000
const POLL_JITTER_MS = 4_000

interface PrepDashboardRevisionPollOptions {
  stationId: number
  enabled: boolean
  onRevisionChanged: () => Promise<void>
}

const usePrepDashboardRevisionPoll = ({
  stationId,
  enabled,
  onRevisionChanged,
}: PrepDashboardRevisionPollOptions): void => {
  const tokenRef = React.useRef<string | null>(null)
  const onRevisionChangedRef = React.useRef(onRevisionChanged)

  React.useEffect(() => {
    onRevisionChangedRef.current = onRevisionChanged
  }, [onRevisionChanged])

  React.useEffect(() => {
    if (!enabled || stationId <= 0) {
      tokenRef.current = null
      return
    }

    let cancelled = false
    let inFlight = false
    let timer: number | null = null
    const controller = new AbortController()

    const schedule = () => {
      if (cancelled) {
        return
      }
      if (timer !== null) {
        window.clearTimeout(timer)
      }
      const delay = MIN_POLL_DELAY_MS + Math.round(Math.random() * POLL_JITTER_MS)
      timer = window.setTimeout(() => void poll(), delay)
    }

    const poll = async () => {
      if (cancelled || inFlight) {
        return
      }
      if (document.visibilityState === 'hidden') {
        schedule()
        return
      }

      inFlight = true
      try {
        const response = await getPrepDashboardRevisions(stationId, { signal: controller.signal })
        if (cancelled) {
          return
        }

        const nextToken = response.revisions?.token ?? ''
        if (!nextToken) {
          return
        }
        if (tokenRef.current === null) {
          tokenRef.current = nextToken
          return
        }
        const snapshotState = usePrepDashboardStore.getState()
        if (tokenRef.current === nextToken && !snapshotState.error) {
          return
        }
        if (snapshotState.loading || snapshotState.refreshing) {
          return
        }

        await onRevisionChangedRef.current()
        if (!cancelled) {
          tokenRef.current = nextToken
        }
      } catch (error) {
        if (!cancelled && (import.meta as any)?.env?.DEV) {
          console.warn('[RPA] Prep revision refresh failed', error)
        }
      } finally {
        inFlight = false
        schedule()
      }
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') {
        return
      }
      if (timer !== null) {
        window.clearTimeout(timer)
        timer = null
      }
      void poll()
    }

    tokenRef.current = null
    document.addEventListener('visibilitychange', handleVisibilityChange)
    void poll()

    return () => {
      cancelled = true
      controller.abort()
      if (timer !== null) {
        window.clearTimeout(timer)
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [enabled, stationId])
}

export default usePrepDashboardRevisionPoll
